import React from 'react'
import {assignInlineVars} from '@vanilla-extract/dynamic'
import {ContactCardGroupContent} from '../../types/cms'
import {SlotComponent} from '../../types/ui'
import {Box, Flex, Stack, Text} from '../../vanilla'
import {Link} from '../link'
import {Sprinkles} from '../../vanilla/sprinkles.css'
import {linkGroupHeadingBackground, headingBgMobileVar, headingBgDesktopVar, faqIconBackground} from './styles.css'
import { isMobile } from '../../utils/utils'
import {useSelectContentTracking} from '../../analytics/select-content-tracking'
import {
  AccountFaqIcon,
  BonusCardFaqIcon,
  ComplaintFaqIcon,
  ContactUsFaqIcon,
  DeliveryFaqIcon,
  OrderFaqIcon,
  PhoneFaqIcon,
  ProductIssuesFaqIcon,
} from '../icons'
import type {Icon} from '../icons'

const faqIconMap: Record<string, Icon> = {
  'account-faq': AccountFaqIcon,
  'bonus-card-faq': BonusCardFaqIcon,
  'complaint-faq': ComplaintFaqIcon,
  'contact-us-faq': ContactUsFaqIcon,
  'delivery-faq': DeliveryFaqIcon,
  'order-faq': OrderFaqIcon,
  'phone-faq': PhoneFaqIcon,
  'product-issues-faq': ProductIssuesFaqIcon,
}

export const ContentContactCardGroup: SlotComponent<ContactCardGroupContent> = ({content}) => {
  const {heading, subHeading, headingAlignment, headingBackground, cardList} = content
  const textAlign = (headingAlignment || 'left').toLowerCase() as Sprinkles['textAlign']
  const sendSelectContentData = useSelectContentTracking()

  if (!content || !cardList?.length) {
    return null
  }

  return (
    <Stack spacing="0px" gap={["0px", "20px"]}>
      {(heading || subHeading) && (
        <Stack
          spacing="8px"
          paddingY="16px"
          paddingX={['28px', '0px']}
          borderBottom={['1px', 'none']}
          borderColor="gray200"
          className={linkGroupHeadingBackground}
          style={assignInlineVars({
            [headingBgMobileVar]: headingBackground?.mobile?.color || 'transparent',
            [headingBgDesktopVar]: headingBackground?.desktop?.color || 'transparent',
          })}
        >
          {heading && (
            <Text
              variant="heading11"
              textAlign={textAlign}
              lineHeight="none"
            >
              {heading}
            </Text>
          )}
          {subHeading && (
            <Text 
              variant="text3" 
              textAlign={textAlign} 
              color="gray800"
            >
              {subHeading}
            </Text>
          )}
        </Stack>
      )}

      <Box
        display={['block', 'grid']}
        style={{gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '20px'}}
      >
        {cardList.map((card, index) => (
          <Link
            key={`${content?._meta?.deliveryId}-card-${index}`}
            href={card.link}
            textDecoration="none"
            display="block"
            maxHeight="168px"
            disableExternalLinkIcon
            onClickCapture={() =>
              sendSelectContentData(content, {
                contentName: card.title,
                contentUrl: card.link,
                contentId: content?._meta?.name ?? heading
              })
            }
          >
            <Flex
              backgroundColor="white"
              alignItems="center"
              paddingY={['16px', '60px']}
              paddingX={['16px', '24px']}
              gap={['10px', '12px']}
              borderBottom={index < cardList.length - 1 ? ['1px', '1px'] : ['none', '1px']}
              borderTop={['none', '1px']}
              borderLeft={['none', '1px']}
              borderRight={['none', '1px']}
              borderRadius={['none', 'base']}
              style={{borderColor: '#D8D8D8'}} // Inline style needed — individual border props override borderColor prop via CSS variable specificity
              height={['auto', 'full']}
            >
              <Box
                className={faqIconBackground}
                borderRadius={['full', 'none']}
                padding={['8px', '0px']}
              >
                {faqIconMap[card.icon] &&
                  React.createElement(faqIconMap[card.icon], {boxSize: ['24px', '64px']})
                }
              </Box>

              <Text 
                variant={isMobile() ? 'text3' : 'heading0'}
                textDecoration={['none', 'underline']}
                lineHeight={["none", "base"]}
              >
                {card.title}
              </Text>
            </Flex>
          </Link>
        ))}
      </Box>
    </Stack>
  )
}
